import AppleIcon from 'assets/svg/apple';
import GoogleIcon from 'assets/svg/google';
import styled from 'styled-components';
import AppleStoreIcon from 'assets/svg/apple-store';
import GooglePlayIcon from 'assets/svg/google-play';
import { useTranslation } from 'react-i18next';
import QRcodeIcon from 'assets/svg/qr-code';
import { useOAuth } from 'providers/OAuthProvider';
import { Button } from '@jouri/components';

interface DownloadAppModalProps {
    onClose: () => void;
    mandatory?: boolean;
}

const ModalOverlay = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    z-index: 1000;
    display: flex;
    align-items: center;
    justify-content: center;
    overflow-y: auto;
    padding: 20px;
`;

const Card = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    align-items: center;
    padding: 48px 32px 32px;
    gap: 32px;
    position: relative;
    width: 90%;
    max-width: 736px;
    background: #ffffff;
    box-shadow: 0px 4px 6px -4px rgba(0, 0, 0, 0.1),
        0px 10px 15px -3px rgba(0, 0, 0, 0.1);
    border-radius: 24px;
    max-height: 90vh;
    overflow-x: hidden;
    overflow-y: auto;

    @media (max-width: 480px) {
        padding: 40px 16px 24px;
        gap: 24px;
        width: 95%;
    }
`;

const CloseButton = styled.button`
    position: absolute;
    top: 16px;
    right: 16px;
    background: transparent;
    border: none;
    font-size: 32px;
    color: #64748b;
    cursor: pointer;
    padding: 0;
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: color 0.2s;
    z-index: 1;

    &:hover {
        color: #0f172a;
    }
`;

const Badge = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
    padding: 2px 10px;
    gap: 4px;
    width: 137px;
    height: 20px;
    border-radius: 6px;
    background: #f1f5f9;
`;

const BadgeText = styled.span`
    font-family: 'Inter', sans-serif;
    font-style: normal;
    font-weight: 500;
    font-size: 14px;
    line-height: 14px;
    display: flex;
    align-items: center;
    text-align: center;
    color: #0f172a;
`;

const Title = styled.h1`
    width: 100%;
    max-width: 672px;
    font-family: 'Inter', sans-serif;
    font-style: normal;
    font-weight: 800;
    font-size: clamp(32px, 5vw, 48px);
    line-height: 1.2;
    text-align: center;
    letter-spacing: -0.012em;
    color: #0f172a;
`;

const Description = styled.p`
    width: 100%;
    max-width: 672px;
    font-family: 'Inter', sans-serif;
    font-style: normal;
    font-weight: 400;
    font-size: clamp(16px, 3vw, 20px);
    line-height: 150%;
    text-align: center;
    color: #64748b;
`;

const QRSection = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 24px;
    gap: 16px;
    width: 100%;
    max-width: 672px;
    background: #f5eefe;
    border-radius: 16px;

    @media (max-width: 480px) {
        padding: 16px;
        gap: 12px;

        svg {
            width: 80px;
            height: auto;
        }
    }
`;

const QRDescription = styled.p`
    width: 349px;
    height: 60px;
    font-family: 'Inter', sans-serif;
    font-style: normal;
    font-weight: 400;
    font-size: 20px;
    line-height: 150%;
    text-align: center;
    color: #64748b;

    @media (max-width: 480px) {
        width: 100%;
        height: auto;
        font-size: 16px;
    }
`;

const AppStoreButtons = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
    flex-wrap: wrap;
    gap: 16px;
    width: 100%;
    max-width: 424px;

    svg {
        max-width: 100%;
        height: auto;
        width: clamp(120px, 30vw, 200px);
    }

    @media (max-width: 480px) {
        flex-direction: column;
        gap: 12px;

        svg {
            width: 160px;
        }
    }
`;

const MobileText = styled.p`
    width: 672px;
    height: 24px;
    font-family: 'Inter', sans-serif;
    font-style: normal;
    font-weight: 500;
    font-size: 16px;
    line-height: 24px;
    text-align: center;
    color: #64748b;

    @media (max-width: 480px) {
        width: 100%;
        height: auto;
    }
`;

const LogoutButton = styled(Button)`
    width: fit-content;
    height: fit-content;
    margin-top: 8px;
`;

const DownloadAppModal = ({
    onClose,
    mandatory = false,
}: DownloadAppModalProps) => {
    const { t } = useTranslation();
    const { logout } = useOAuth();

    const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
        if (e.target === e.currentTarget && !mandatory) {
            onClose();
        }
    };

    const handleLogout = () => {
        logout();
    };

    return (
        <ModalOverlay onClick={handleOverlayClick}>
            <Card>
                {!mandatory && <CloseButton onClick={onClose}>×</CloseButton>}
                <Badge>
                    <BadgeText>{t('availableOn')}</BadgeText>
                    <AppleIcon />
                    <GoogleIcon />
                </Badge>
                <Title>
                    {mandatory ? t('incompleteAccountTitle') : t('downloadApp')}
                </Title>
                <Description>
                    {mandatory
                        ? t('incompleteAccountMessage')
                        : t('createAccountMessage')}
                </Description>
                <QRSection>
                    <QRcodeIcon style={{ width: '200px', height: '200px' }} />
                    <QRDescription>{t('qrCodeInstruction')}</QRDescription>
                    <AppStoreButtons>
                        <AppleStoreIcon
                            onClick={() =>
                                window.open(
                                    'https://apps.apple.com/sa/app/mis-pay-%D8%A7%D9%85-%D8%A3%D9%8A-%D8%A7%D8%B3-%D8%A8%D8%A7%D9%8A/id6443602765'
                                )
                            }
                        />
                        <GooglePlayIcon
                            onClick={() =>
                                window.open(
                                    'https://play.google.com/store/apps/details?id=com.mispaymobile&hl=en'
                                )
                            }
                        />
                    </AppStoreButtons>
                </QRSection>
                {mandatory && (
                    <LogoutButton onClick={handleLogout}>
                        {t('logoutButton')}
                    </LogoutButton>
                )}
                <MobileText>
                    {t('onMobileGetApp')}{' '}
                    <a
                        href="https://app.mispay.co/"
                        style={{
                            color: '#5947F2',
                            fontSize: '16px',
                            fontFamily: 'Inter, sans-serif',
                            fontStyle: 'normal',
                            fontWeight: 500,
                            lineHeight: '24px',
                        }}
                        target="_blank"
                    >
                        {t('getApp')}
                    </a>
                </MobileText>
            </Card>
        </ModalOverlay>
    );
};

export default DownloadAppModal;
